'use client';

import { useCallback, useEffect, useMemo } from 'react';
import {
  useCheckoutStore,
  CHECKOUT_STEPS,
  selectBonusState,
  type CheckoutStep,
} from '@/stores/checkout-store';
import { useAuthStore, selectIsAuthenticated } from '@/stores/auth-store';
import { useFirstOrderQuery } from './use-first-order';

/**
 * Порядок оформления живёт в checkout-store. Здесь скрываем подарок за
 * первый заказ для постоянного клиента. Рулетка больше не является шагом
 * оформления, поэтому её конфиг не влияет на переходы и прогресс корзины.
 */

/** Первый ли это заказ. Пишет ответ в checkout-store, считает один раз. */
function useFirstOrderCheck(enabled: boolean) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const setBonusState = useCheckoutStore(state => state.setBonusState);
  const orders = useFirstOrderQuery(enabled);

  // The request belongs to Query, not to an effect that cancels itself when
  // isCheckingFirstOrder changes. Re-evaluate per account and checkout opening.
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    if (orders.isPending || orders.isFetching) {
      setBonusState({ isCheckingFirstOrder: true, isFirstOrderChecked: false });
      return;
    }
    setBonusState({
      isFirstOrder: orders.isSuccess && orders.data.length === 0,
      isFirstOrderChecked: true,
      isCheckingFirstOrder: false,
      firstOrderCheckSuccess: orders.isSuccess,
    });
  }, [enabled, isAuthenticated, orders.isPending, orders.isFetching, orders.isSuccess, orders.data, setBonusState]);
}

export interface VisibleCheckoutSteps {
  /** Шаги, которые человек реально увидит, в порядке прохождения. */
  steps: CheckoutStep[];
  /** Позиция текущего шага среди видимых (0-based). -1, если шаг скрыт. */
  index: number;
  /** Прогресс в процентах — по видимым шагам. */
  progress: number;
  /** Следующий/предыдущий видимый шаг или null, если дальше некуда. */
  nextVisibleStep: () => CheckoutStep | null;
  prevVisibleStep: () => CheckoutStep | null;
}

export function useVisibleCheckoutSteps(isOpen: boolean): VisibleCheckoutSteps {
  const currentStep = useCheckoutStore((state) => state.currentStep);
  const setStep = useCheckoutStore((state) => state.setStep);
  const bonusState = useCheckoutStore(selectBonusState);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  useFirstOrderCheck(isOpen);

  const steps = useMemo<CheckoutStep[]>(() => {
    // Гостю шаг нужен: подарок ему ещё положен, там кнопка «Войти и получить».
    // Скрываем только у того, кто подарок уже забрал.
    const bonusVisible = !(
      isAuthenticated &&
      bonusState.isFirstOrderChecked &&
      !bonusState.isFirstOrder
    );

    return CHECKOUT_STEPS.filter((step) => {
      if (step === 'bonus') return bonusVisible;
      return true;
    });
  }, [isAuthenticated, bonusState.isFirstOrderChecked, bonusState.isFirstOrder]);

  const index = steps.indexOf(currentStep);

  // Если проверка первого заказа скрыла текущий шаг, продолжаем оформление.
  useEffect(() => {
    if (!isOpen) return;
    if (steps.includes(currentStep)) return;

    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    const forward = steps.find((step) => CHECKOUT_STEPS.indexOf(step) > orderIndex);
    if (forward) setStep(forward);
    else if (steps.length > 0) setStep(steps[steps.length - 1]);
  }, [isOpen, steps, currentStep, setStep]);

  const nextVisibleStep = useCallback((): CheckoutStep | null => {
    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    return steps.find((step) => CHECKOUT_STEPS.indexOf(step) > orderIndex) ?? null;
  }, [steps, currentStep]);

  const prevVisibleStep = useCallback((): CheckoutStep | null => {
    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    const earlier = steps.filter((step) => CHECKOUT_STEPS.indexOf(step) < orderIndex);
    return earlier.length > 0 ? earlier[earlier.length - 1] : null;
  }, [steps, currentStep]);

  const progress = steps.length > 0 ? ((Math.max(index, 0) + 1) / steps.length) * 100 : 0;

  // Стабильная ссылка: результат уходит в зависимости useCallback у обработчиков
  // корзины, и новый объект на каждый рендер пересоздавал бы их все.
  return useMemo(
    () => ({ steps, index, progress, nextVisibleStep, prevVisibleStep }),
    [steps, index, progress, nextVisibleStep, prevVisibleStep]
  );
}
